import type {
  IClaimDetails,
  IClaimFilters,
  IClaimStatusUpdate,
  IClaimStatusesResponse,
  CreateClaimInput,
  UpdateClaimInput,
  IClaimCostsForm,
} from '@/types/asicw/claims'
import {
  createClaim,
  deleteClaim,
  deleteBulkClaims,
  getClaimDetails,
  getClaims,
  getClaimStatuses,
  updateClaim,
  updateClaimStatus,
  updateClaimCosts,
} from '@/services/asicw/claims.service'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import type { ComputedRef } from 'vue'
import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'

const relatedQueryType = ['table', 'details']

const invalidateRelatedQueries = (query: any) => relatedQueryType.includes(query.queryKey[1]?.type)

export function useClaims() {
  const queryClient = useQueryClient()

  const getClaimsQuery = (filters: IClaimFilters) =>
    useQuery({
      queryKey: ['claims', { type: 'table' }, filters],
      queryFn: () => getClaims(filters),
      placeholderData: (previousData) => previousData,
    })

  const getClaimDetailsQuery = (claimNumber: ComputedRef<string | undefined>) =>
    useQuery<IClaimDetails>({
      queryKey: ['claims', { type: 'details' }, claimNumber],
      queryFn: () => getClaimDetails(claimNumber.value!),
      enabled: () => !!claimNumber.value,
    })

  const getClaimStatusesQuery = () =>
    useQuery<IClaimStatusesResponse>({
      queryKey: ['claims', { type: 'statuses' }],
      queryFn: () => getClaimStatuses(),
    })

  const createClaimMutation = useMutation({
    mutationFn: (claims: CreateClaimInput[]) => createClaim(claims),
    onSuccess: () => {
      NotifySuccess('Claim created successfully')
      queryClient.invalidateQueries({ predicate: invalidateRelatedQueries })
    },
    onError: NotifyErrorWrapper('Failed to create claim'),
  })

  const updateClaimMutation = useMutation({
    mutationFn: ({ claimNumber, data }: { claimNumber: string; data: UpdateClaimInput }) =>
      updateClaim(claimNumber, data),
    onSuccess: () => {
      NotifySuccess('Claim updated successfully.')
      queryClient.invalidateQueries({ predicate: invalidateRelatedQueries })
    },
    onError: NotifyErrorWrapper('Failed to update claim'),
  })

  const updateClaimStatusMutation = useMutation({
    mutationFn: ({ claimNumber, statusUpdate }: { claimNumber: string; statusUpdate: IClaimStatusUpdate }) =>
      updateClaimStatus(claimNumber, statusUpdate),
    onSuccess: () => {
      NotifySuccess('Claim status updated successfully.')
      queryClient.invalidateQueries({ predicate: invalidateRelatedQueries })
    },
    onError: NotifyErrorWrapper('Failed to update claim status'),
  })

  const updateClaimCostsMutation = useMutation({
    mutationFn: ({ claimNumber, form }: { claimNumber: string; form: IClaimCostsForm }) =>
      updateClaimCosts(claimNumber, form),
    onSuccess: () => {
      NotifySuccess('Claim costs updated successfully.')
      queryClient.invalidateQueries({ queryKey: ['claims'] })
    },
    onError: NotifyErrorWrapper('Failed to update claim costs'),
  })

  const deleteClaimMutation = useMutation({
    mutationFn: (claimNumber: string) => deleteClaim(claimNumber),
    onSuccess: () => {
      NotifySuccess('Claim deleted successfully.')
      queryClient.invalidateQueries({ predicate: invalidateRelatedQueries })
    },
    onError: NotifyErrorWrapper('Failed to delete claim'),
  })

  const deleteBulkClaimsMutation = useMutation({
    mutationFn: (claimIds: string[]) => deleteBulkClaims(claimIds),
    onSuccess: () => {
      NotifySuccess('Claims deleted successfully.')
      queryClient.invalidateQueries({ predicate: invalidateRelatedQueries })
    },
    onError: NotifyErrorWrapper('Failed to delete claims'),
  })

  return {
    getClaimsQuery,
    getClaimDetailsQuery,
    getClaimStatusesQuery,
    createClaimMutation,
    updateClaimMutation,
    updateClaimStatusMutation,
    updateClaimCostsMutation,
    deleteClaimMutation,
    deleteBulkClaimsMutation,
  }
}
